import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { MapPin, Phone } from "lucide-react";
import { TiktokIcon, FacebookIcon, InstagramIcon } from "@/components/shared/svg/Icons";

interface BoutiqueCardProps {
    city: string;
    address: string;
    phone: string;
    image: string;
    latitude?: number | undefined;
    longitude?: number | undefined;
    facebookUrl?: string | undefined;
    instagramUrl?: string | undefined;
    tiktokUrl?: string | undefined;
    className?: string | undefined;
}

export const BoutiqueCard = ({
    city,
    address,
    phone,
    image,
    latitude,
    longitude,
    facebookUrl,
    instagramUrl,
    tiktokUrl,
    className,
}: BoutiqueCardProps) => {
    return (
        <div className={cn("bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col h-full border border-gray-100", className)}>
            {/* Image Section */}
            <div className="relative h-64 w-full">
                <Image
                    src={image}
                    alt={`${city} boutique`}
                    fill
                    className="object-cover"
                />
            </div>

            {/* Content Section */}
            <div className="p-6 flex flex-col grow">
                <h3 className="font-brastika text-2xl text-primary-900 mb-4">{city}</h3>
                
                <div className="space-y-3 mb-6 grow">
                    <div className="flex items-start gap-3 text-primary-700">
                        <MapPin className="w-5 h-5 mt-1 shrink-0 text-primary-500" />
                        <p className="text-sm leading-relaxed">{address}</p>
                    </div>
                    <div className="flex items-center gap-3 text-primary-700">
                        <Phone className="w-5 h-5 shrink-0 text-primary-500" />
                        <p className="text-sm font-medium">{phone}</p>
                    </div>
                </div>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
                    <div className="flex gap-2">
                        {facebookUrl && (
                            <Link href={facebookUrl} className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-900 transition-all">
                                <FacebookIcon className="w-4 h-4" />
                            </Link>
                        )}
                        {instagramUrl && (
                            <Link href={instagramUrl} className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-900 transition-all">
                                <InstagramIcon className="w-4 h-4" />
                            </Link>
                        )}
                        {tiktokUrl && (
                            <Link href={tiktokUrl} className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-900 transition-all">
                                <TiktokIcon className="w-4 h-4" />
                            </Link>
                        )}
                    </div>
                    
                    <Link 
                        href={latitude && longitude 
                            ? `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}` 
                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-2 bg-primary-500 text-white text-xs font-semibold rounded-lg hover:bg-primary-600 transition-colors uppercase tracking-wider text-center"
                    >
                        Visiter
                    </Link>
                </div>
            </div>
        </div>
    );
};
